import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Box, Typography, Button, CircularProgress, Grid, Divider } from '@mui/material';
import axios from 'axios';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { RequestStatus } from '../../../../enums/RequestStatusEnum';

const EditInvoiceModal = ({ open, onClose, invoiceNumber, invoiceDetails, getValues, prefillingData }) => {
  const [loading, setLoading] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [error, setError] = useState(null);
   const navigate = useNavigate();
  const [isEditInvoiceCreated, setIsEditInvoiceCreated] = useState(false);
  const [isRequestApproved, setIsRequestApproved] = useState(false);
   const [isEditInvoiceId, setIsEditInvoiceId] = useState(false);

  useEffect(() => {
    if (open) {
      fetchEditItems();
    }
  }, [open]);

  const fetchEditItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/AdminInvoiceEdit/${invoiceNumber}?requestType=${RequestStatus.General}`);
      const data = response.data;
      setIsEditInvoiceCreated(data?.isAnyResponseOnEditCreditInvoice);
      setIsRequestApproved(data?.isRequestApproved);
      setIsEditInvoiceId(data?.id);
      if (data && data.invoiceEditItems) {
        setEditItems(data.invoiceEditItems);
      } else {
        setEditItems([]);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to fetch data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (IsEditInvoiceApproved) => {
    try {
      setLoading(true);
      const { getInvoiceData } = prefillingData;

      const response = await axios.post(`/AdminInvoiceEdit/update-edit-invoice/${isEditInvoiceId}?IsEditInvoiceApproved=${IsEditInvoiceApproved}`);

      if (response.status === 200) {
        toast.success(response.data?.message || 'Invoice updated successfully');
        navigate(`/dashboard/invoices`);
        onClose();
      } else {
        toast.error(response.data?.message || 'Failed to update invoice. Please try again.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'An error occurred while updating the invoice.');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`/AdminInvoiceEdit/${invoiceNumber}`);
      toast.success(response.data?.message || 'Edit request rejected');
      navigate(`/dashboard/invoices`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to reject the edit request.');
    } finally {
      setLoading(false);
    }
  };

  // Helper to compare values ignoring formatting (e.g., 1000 vs 1000.00)
const areValuesEqual = (a, b) => {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  // Try to compare as numbers if possible
  const numA = parseFloat(a);
  const numB = parseFloat(b);
  if (!isNaN(numA) && !isNaN(numB)) {
    return numA === numB;
  }
  // Otherwise, compare as trimmed strings
  return String(a).trim() === String(b).trim();
};

const renderFieldWithDifference = (fieldName, previousValue, currentValue, originValue, requestedValue, isDateTime = false) => {
  // For date/time fields, compare the raw values
  if (isDateTime) {
    // Compare formatted date-time strings
    const prevFormatted = originValue ? dayjs(originValue, ['DD-MM-YYYY HH:mm:ss', 'YYYY-MM-DDTHH:mm:ss']).format('YYYY-MM-DD HH:mm:ss') : '';
    const reqFormatted = requestedValue ? dayjs(requestedValue, ['DD-MM-YYYY HH:mm:ss', 'YYYY-MM-DDTHH:mm:ss']).format('YYYY-MM-DD HH:mm:ss') : '';
    if (prevFormatted === reqFormatted) return null;
  } else {
    if (areValuesEqual(originValue, requestedValue)) return null;
  }
  const prevVal = originValue === '' ? 'N/A' : (originValue ?? previousValue);
  // If requestedValue and currentValue are both falsy or 'No Change', do not show
  if (!requestedValue && (!currentValue || currentValue === 'No Change')) return null;
  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid #ddd',
        borderRadius: 2,
        bgcolor: '#f5f5f5',
        mb: 2,
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Typography variant="subtitle1" fontWeight="bold" mb={1} color="primary">
        {fieldName}:
      </Typography>
      <Typography variant="body2" sx={{ color: 'gray', fontStyle: 'italic' }}>
        Previous Value: {isDateTime && prevVal ? dayjs(prevVal, ['DD-MM-YYYY HH:mm:ss', 'YYYY-MM-DDTHH:mm:ss']).format('YYYY-MM-DD HH:mm:ss') : prevVal}
      </Typography>
      <Typography variant="body2" fontWeight="bold" color="text.primary">
        Requested Value: {isDateTime && requestedValue ? dayjs(requestedValue, ['DD-MM-YYYY HH:mm:ss', 'YYYY-MM-DDTHH:mm:ss']).format('YYYY-MM-DD HH:mm:ss') : (requestedValue ?? currentValue) || 'No Change'}
      </Typography>
    </Box>
  );
};

  // Helper to render working hours changes
const renderWorkingHoursChanges = () => {
  // Filter for both added and updated working hours
  const workingHoursItems = editItems.filter(
    (item) =>
      item.fieldName.startsWith('WorkingHoursAdded') ||
      item.fieldName.startsWith('WorkingHoursUpdated')
  );
  if (workingHoursItems.length === 0) return <Typography>No working hours changes</Typography>;

  // Field labels in order
  const fieldLabels = [
    'Amount',
    'AmountPerUnit',
    'Unit',
    'Vat',
    'WorkingDate',
    'WorkingHour',
    'ArticleNumber',
    'UserName',
    'FoodAllowance'
  ];

  // Helper to format field name
  const formatFieldName = (str) =>
    str.replace(/([a-z])([A-Z])/g, '$1 $2').replace('Added', ' Added').replace('Updated', ' Updated').replace(/  +/g, ' ').trim();

  return (
    <Box>
      {workingHoursItems.map((item, idx) => {
        let requestedObj = {};
        let originObj = {};
        try {
          requestedObj = item.requestedValue ? JSON.parse(item.requestedValue) : {};
        } catch (e) {}
        try {
          originObj = item.originValue ? JSON.parse(item.originValue) : {};
        } catch (e) {}
        // If all values are equal, skip rendering this item
        const allEqual = fieldLabels.every(label => areValuesEqual(requestedObj[label], originObj[label])); 
        if (allEqual) return null;
        return (
          <Box key={idx} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="subtitle2" fontWeight="bold" color="primary" mb={1}>
              {formatFieldName(item.fieldName.split(' - ')[0])}
            </Typography>
            <Grid container spacing={1}>
              {fieldLabels.map((label, i) => {
                if (areValuesEqual(requestedObj[label], originObj[label])) return null;
                if (label === 'ArticleNumber') {
                  return (
                    <Grid item xs={6} sm={4} key={label}>
                      <Typography variant="body2" fontWeight="bold">Article:</Typography>
                      <Typography variant="body2">
                        Previous Value: {originObj['ArticleName'] || originObj[label] || '-'}<br/>
                        Requested Value: {requestedObj['ArticleName'] || requestedObj[label] || '-'
}
                      </Typography>
                    </Grid>
                  );
                }
                return (
                  <Grid item xs={6} sm={4} key={label}>
                    <Typography variant="body2" fontWeight="bold">{label}:</Typography>
                    <Typography variant="body2">
                      Previous Value: {originObj[label] === '' || originObj[label] == null ? 'N/A' : originObj[label]}<br/>
                      Requested Value: {requestedObj[label] || '-'
}
                    </Typography>
                  </Grid>
                );
              })}
            </Grid>
            {item.userComment && (
              <Typography variant="body2" color="textSecondary" mt={1}>
                Comment: {item.userComment}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="edit-invoice-modal-title">
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 700,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 3,
          overflowY: 'auto',
          maxHeight: '90vh',
        }}
      >
        <Typography
          id="edit-invoice-modal-title"
          variant="h5"
          component="h2"
          mb={3}
          textAlign="center"
          color="primary"
          fontWeight="bold"
        >
          Edit Invoice Request  
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {(() => {
          if (loading) {
            return (
              <Box display="flex" justifyContent="center" alignItems="center" height="100px">
                <CircularProgress />
              </Box>
            );
          }

          if (error) {
            return (
              <Typography color="error" textAlign="center" fontWeight="bold">
                {error}
              </Typography>
            );
          }

          if (editItems.length > 0) {
            return (
              <Box>
                <Grid container spacing={2}>
                  {(() => {
                    const vatItem = editItems.find((item) => item.fieldName === 'Vat');
                    if (!vatItem) return null;
                    const field = renderFieldWithDifference(
                      'VAT',
                      `${invoiceDetails?.vat || 0}%`,
                      `${vatItem.requestedValue || 0}%`,
                      vatItem.originValue,
                      vatItem.requestedValue
                    );
                    return field ? <Grid item xs={12} sm={6}>{field}</Grid> : null;
                  })()}

                  {(() => {
                    const dueDateItem = editItems.find((item) => item.fieldName === 'DueDate');
                    // Always show DueDate if present
                    if (dueDateItem) {
                      return (
                        <Grid item xs={12} sm={6}>
                          <Box
                            sx={{
                              p: 2,
                              border: '1px solid #ddd',
                              borderRadius: 2,
                              bgcolor: '#f5f5f5',
                              mb: 2,
                              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                            }}
                          >
                            <Typography variant="subtitle1" fontWeight="bold" mb={1} color="primary">
                              Due Date:
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'gray', fontStyle: 'italic' }}>
                              Previous Value: {dueDateItem.originValue ? dayjs(dueDateItem.originValue, 'DD-MM-YYYY HH:mm:ss').isValid() ? dayjs(dueDateItem.originValue, 'DD-MM-YYYY HH:mm:ss').format('YYYY-MM-DD HH:mm:ss') : dueDateItem.originValue : 'N/A'}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="text.primary">
                              Requested Value: {dueDateItem.requestedValue ? dayjs(dueDateItem.requestedValue, 'DD-MM-YYYY HH:mm:ss').isValid() ? dayjs(dueDateItem.requestedValue, 'DD-MM-YYYY HH:mm:ss').format('YYYY-MM-DD HH:mm:ss') : dueDateItem.requestedValue : 'No Change'}
                            </Typography>
                          </Box>
                        </Grid>
                      );
                    }
                    return null;
                  })()}

                  {(() => {
                    const field = renderFieldWithDifference(
                      'Holiday Savings Fee',
                      invoiceDetails?.holidaySavingsFee || 'N/A',
                      editItems.find((item) => item.fieldName === 'HolidaySavingsFee')?.requestedValue,
                      editItems.find((item) => item.fieldName === 'HolidaySavingsFee')?.originValue,
                      editItems.find((item) => item.fieldName === 'HolidaySavingsFee')?.requestedValue
                    );
                    return field ? <Grid item xs={12} sm={6}>{field}</Grid> : null;
                  })()}
                  {(() => {
                    const field = renderFieldWithDifference(
                      'Amount',
                      invoiceDetails?.amount || 'N/A',
                      editItems.find((item) => item.fieldName === 'Amount')?.requestedValue,
                      editItems.find((item) => item.fieldName === 'Amount')?.originValue,
                      editItems.find((item) => item.fieldName === 'Amount')?.requestedValue
                    );
                    return field ? <Grid item xs={12} sm={6}>{field}</Grid> : null;
                  })()}
                   {(() => {
                    const field = renderFieldWithDifference(
                      'Currency',
                      invoiceDetails?.currency || 'N/A',
                      editItems.find((item) => item.fieldName === 'Currency')?.requestedValue,
                      editItems.find((item) => item.fieldName === 'Currency')?.originValue,
                      editItems.find((item) => item.fieldName === 'Currency')?.requestedValue
                    );
                    return field ? <Grid item xs={12} sm={6}>{field}</Grid> : null;
                  })()}
                   {(() => {
                    const field = renderFieldWithDifference(
                      'Pension Amount',
                      editItems.find((item) => item.fieldName === 'PensionAmount')?.originValue,
                      editItems.find((item) => item.fieldName === 'PensionAmount')?.requestedValue,
                      editItems.find((item) => item.fieldName === 'PensionAmount')?.originValue,
                      editItems.find((item) => item.fieldName === 'PensionAmount')?.requestedValue
                    );
                    return field ? <Grid item xs={12} sm={6}>{field}</Grid> : null;
                  })()}
                   <Grid item xs={12} sm={12}>
                    {/* Working Hours section updated to show all changes */}
                    {renderWorkingHoursChanges()}
                  </Grid>
                  {/* Show WorkingHoursRemoved items */}
                  {(() => {
                    const removedItems = editItems.filter(item => item.fieldName === 'WorkingHoursRemoved');
                    if (removedItems.length === 0) return null;
                    // Field labels for removed working hours
                    const fieldLabels = [
                      'Amount',
                      'AmountPerUnit',
                      'Unit',
                      'Vat',
                      'WorkingDate',
                      'WorkingHour',
                      'ArticleNumber',
                      'UserName',
                      'RowId',
                      'FoodAllowance'
                    ];
                    return (
                      <Box sx={{ mt: 2 }}>
                        {removedItems.map((item, idx) => {
                          let originObj = {};
                          try {
                            originObj = item.originValue ? JSON.parse(item.originValue) : {};
                          } catch (e) {}
                          return (
                            <Box key={idx} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 2, bgcolor: '#f5f5f5' }}>
                              <Typography variant="subtitle2" fontWeight="bold" color="error" mb={1}>
                                Working Hours Removed
                              </Typography>
                              <Grid container spacing={1}>
                                {fieldLabels.map((label, i) => (
                                  label === 'ArticleNumber' ? (
                                    <Grid item xs={6} sm={4} key={label}>
                                      <Typography variant="body2" fontWeight="bold">Article:</Typography>
                                      <Typography variant="body2">
                                        {originObj['ArticleName'] || originObj[label] || '-'}
                                      </Typography>
                                    </Grid>
                                  ) : (
                                    <Grid item xs={6} sm={4} key={label}>
                                      <Typography variant="body2" fontWeight="bold">{label}:</Typography>
                                      <Typography variant="body2">
                                        {originObj[label] || '-'}
                                      </Typography>
                                    </Grid>
                                  )
                                ))}
                              </Grid>
                              {item.userComment && (
                                <Typography variant="body2" color="textSecondary" mt={1}>
                                  Comment: {item.userComment}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  })()}
                   {(() => {
                    const field = renderFieldWithDifference(
                      'Reference Name',
                      invoiceDetails?.customer?.reference || 'N/A',
                      editItems.find((item) => item.fieldName === 'ReferenceName')?.requestedValue,
                      editItems.find((item) => item.fieldName === 'ReferenceName')?.originValue,
                      editItems.find((item) => item.fieldName === 'ReferenceName')?.requestedValue
                    );
                    return field ? <Grid item xs={12} sm={6}>{field}</Grid> : null;
                  })()}
                  {(() => {
                    const field = renderFieldWithDifference(
                      'Description',
                      invoiceDetails?.description,
                      editItems.find((item) => item.fieldName === 'Description')?.requestedValue,
                      editItems.find((item) => item.fieldName === 'Description')?.originValue,
                      editItems.find((item) => item.fieldName === 'Description')?.requestedValue
                    );
                    return field ? <Grid item xs={12}>{field}</Grid> : null;
                  })()}
                </Grid>
                {/* Show PensionAmount and HolidaySavingsFee changes per freelancer */}
                {(() => {
                  // Filter editItems for PensionAmount and HolidaySavingsFee with freelancer name
                  const relevantFields = ['PensionAmount', 'HolidaySavingsFee'];
                  const items = editItems.filter(item => {
                    return relevantFields.some(f => item.fieldName.startsWith(f + ' - '));
                  });
                  if (items.length === 0) return null;
                  return (
                    <Box sx={{ mt: 2 }}>
                      {items.map((item, idx) => {
                        // Extract field and freelancer name
                        const [field, freelancer] = item.fieldName.split(' - ');
                        if (areValuesEqual(item.originValue, item.requestedValue)) return null;
                        return (
                          <Box key={idx} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 2, bgcolor: '#f5f5f5' }}>
                            <Typography variant="subtitle2" fontWeight="bold" color="primary" mb={1}>
                              {field} ({freelancer})
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'gray', fontStyle: 'italic' }}>
                              Previous Value: {item.originValue === '' || item.originValue == null ? 'N/A' : item.originValue}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="text.primary">
                              Requested Value: {item.requestedValue || 'No Change'}
                            </Typography>
                            {item.userComment && (
                              <Typography variant="body2" color="textSecondary" mt={1}>
                                Comment: {item.userComment}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })()}
              </Box>
            );
          }

          return (
            <Typography textAlign="center" color="textSecondary" fontStyle="italic">
              No edits requested by user
            </Typography>
          );
        })()}

        <Divider sx={{ mt: 3, mb: 3 }} />
        <div className='d-flex' style={{justifyContent: 'space-between',alignItems: "baseline"}}>
        {isEditInvoiceCreated ? (
          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 'bold',
              color: isRequestApproved ? 'green' : 'red',
            }}
          >
            {isRequestApproved ? 'Accepted' : 'Rejected'}
          </Typography>
        ):<p> </p>}
        <Box display="flex">
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              bgcolor: '#6c757d',
              '&:hover': { bgcolor: '#5a6268' },
              color: 'white',
            }}
            className='mr-2'
          >
            Close
          </Button>
          {editItems.length > 0 && (
            <>
              {!isEditInvoiceCreated && (
                <Button
                 onClick={() => handleSubmit(false)}
                  variant="contained"
                  sx={{
                    bgcolor: '#6c757d',
                    '&:hover': { bgcolor: '#5a6268' },
                    color: 'white',
                  }}
                  disabled={loading}
                >
                  Reject
                </Button>
              )}

              {!isEditInvoiceCreated && (
                <Button onClick={() => handleSubmit(true)} variant="contained" color="primary" className='ml-2' disabled={loading}>
                  Accept
                </Button>
              )}
              
            </>
          )}
        </Box>
        </div>
       
      </Box>
    </Modal>
  );
};

export default EditInvoiceModal;
